import { action, computed, observable, runInAction } from 'mobx';
import { creditCardsApi } from 'AppClient/api/CreditCards';
import { CreditCard, BookingCreditCard, CustomerPaymentMethod } from 'app-types';
import { FetchStatus } from 'servicesCommon/apiTypes';
import { RootStore } from 'AppClient/stores';
import { ReactStripeElements } from 'react-stripe-elements';

export class BookingCCStore {
	@observable public _creditCardsForBooking?: BookingCreditCard[];
	@observable public _availableCreditCards?: BookingCreditCard[];
	@observable public _attachedBookingCardId?: number;
	@observable public _oneTimeCreditCards: BookingCreditCard[] = [];
	@observable public _oneTimeCardId?: string | number;
	@observable public _isInitialCardDeleted?: boolean;
	@observable public _isInitialCardExpired?: boolean;
	@observable public _fetchStatus: FetchStatus = FetchStatus.IDLE;
	@observable private _isCreditCardUser?: boolean;

	constructor(private rootStore: RootStore) {}

	@computed
	get isPending(): boolean {
		return this._fetchStatus === FetchStatus.PENDING;
	}

	@computed
	get fetchStatus(): FetchStatus {
		return this._fetchStatus;
	}

	@action
	setUserPaymentType(type: CustomerPaymentMethod) {
		this._isCreditCardUser = type === CustomerPaymentMethod.creditCard;
	}

	@computed
	get isInitialCardDeleted(): boolean | undefined {
		return this._isInitialCardDeleted;
	}

	@computed
	get isInitialCardExpired(): boolean | undefined {
		return this._isInitialCardExpired;
	}

	@computed
	get isCreditCardUser(): boolean | undefined {
		return this._isCreditCardUser;
	}

	@computed
	get creditCardsForBooking(): BookingCreditCard[] | undefined {
		return this._creditCardsForBooking;
	}

	@computed
	get availableCreditCards(): BookingCreditCard[] | undefined {
		return this._availableCreditCards;
	}

	@computed
	get oneTimeCardId(): string | number | undefined {
		return this._oneTimeCardId;
	}

	@computed
	get oneTimeCreditCards(): BookingCreditCard[] {
		return this._oneTimeCreditCards;
	}

	@computed
	get currentBookingCardId(): string | number | undefined {
		return this._oneTimeCardId || this._attachedBookingCardId;
	}

	@computed
	get currentBookingCard(): BookingCreditCard | CreditCard | undefined {
		if (this._oneTimeCardId) {
			return this._oneTimeCreditCards.filter(
				item => item.customerUserCreditCardId === this._oneTimeCardId
			)[0];
		}
		if (this._creditCardsForBooking && this._attachedBookingCardId) {
			return this._creditCardsForBooking.filter(
				item => item.customerUserCreditCardId === this._attachedBookingCardId
			)[0];
		}
		return undefined;
	}

	@computed
	get isBookingCreditCardValid(): boolean {
		return (
			!this.isCreditCardUser ||
			Boolean(this._attachedBookingCardId) ||
			Boolean(this.oneTimeCardId)
		);
	}

	@action.bound
	async getCreditCardByBookingId(bookingId: number) {
		this._fetchStatus = FetchStatus.PENDING;

		if (!this.isCreditCardUser) {
			return undefined;
		}

		try {
			const response = await creditCardsApi.getBookingCard({
				bookingId,
			});
			const {
				data: { result, error },
			} = response;
			if (error) {
				this.rootStore.alertStore.setAlert(error, 'error');
				this._fetchStatus = FetchStatus.FAILURE;
			}
			if (result) {
				runInAction(() => {
					this._fetchStatus = FetchStatus.SUCCESS;
				});
				return result;
			}
		} catch (error) {
			this.rootStore.alertStore.setRequestError(error);
			this._fetchStatus = FetchStatus.FAILURE;
		}
		return;
	}

	@action.bound
	async getCreditCardsForBooking() {
		this._fetchStatus = FetchStatus.PENDING;

		try {
			const response = await creditCardsApi.getCardsForBooking();
			const {
				data: { result, error },
			} = response;
			if (error) {
				this.rootStore.alertStore.setAlert(error, 'error');
				this._fetchStatus = FetchStatus.FAILURE;
				return;
			}
			runInAction(() => {
				this.setBookingCreditCards(result || []);
				this.setInitialValue(result || []);
				this._fetchStatus = FetchStatus.SUCCESS;
			});
		} catch (_) {
			this._fetchStatus = FetchStatus.FAILURE;
		}
	}

	@action.bound
	setBookingCreditCards(allCreditCards: BookingCreditCard[]) {
		this._creditCardsForBooking = allCreditCards;
		this._availableCreditCards = allCreditCards.filter(
			item => !item.isDeleted && !item.isExpired && !item.isOneTimeCard
		);
		this._oneTimeCreditCards = allCreditCards.filter(
			item =>
				item.isOneTimeCard && item.customerUserCreditCardId === this._attachedBookingCardId
		) as any;
	}

	@action.bound
	setInitialValue(allCreditCards: BookingCreditCard[]) {
		const availableCreditCards = this.availableCreditCards as BookingCreditCard[];

		if (
			!this._attachedBookingCardId &&
			!availableCreditCards.length &&
			!this.oneTimeCreditCards.length
		) {
			// set empty value in case if it is not amending, and no available cards
			this.setBookingCardId(undefined);
		}

		if (!this._attachedBookingCardId && availableCreditCards.length) {
			// if no predefined credit card -> set first in the list
			this.setBookingCardId(availableCreditCards[0].customerUserCreditCardId);
		}

		const creditCard = allCreditCards.filter(
			item => item.customerUserCreditCardId === this._attachedBookingCardId
		)[0];

		if (
			this._attachedBookingCardId &&
			((creditCard.isDeleted && !creditCard.isOneTimeCard) || creditCard.isExpired)
		) {
			// if we have predefined card (on amending) and it is not expired or deleted -> set first in the list
			this.setBookingCardId(
				availableCreditCards[0] && availableCreditCards[0].customerUserCreditCardId
			);

			if (!creditCard.isOneTimeCard && creditCard.isDeleted) {
				this._isInitialCardDeleted = true;
			}
			if (creditCard.isExpired) {
				this._isInitialCardExpired = true;
			}
		}
	}

	@action.bound
	async addOneTimeCreditCard(
		stripe: ReactStripeElements.StripeProps,
		cardElement: stripe.elements.Element
	) {
		const { getPaymentMethodIdFromStripe, getCreditCardInfo } = this.rootStore.ccStore;
		this._fetchStatus = FetchStatus.PENDING;
		try {
			const paymentMethodId = await getPaymentMethodIdFromStripe(stripe, cardElement);

			if (paymentMethodId) {
				const addedCardInfo = await getCreditCardInfo(paymentMethodId);

				if (addedCardInfo) {
					runInAction(() => {
						this._oneTimeCreditCards.push({
							...addedCardInfo,
							customerUserCreditCardId: paymentMethodId,
						});

						this.setBookingCardId(paymentMethodId, true);

						this._fetchStatus = FetchStatus.SUCCESS;
					});
				} else {
					this._fetchStatus = FetchStatus.FAILURE;
				}
			}
			if (!paymentMethodId) {
				this._fetchStatus = FetchStatus.FAILURE;
			}
		} catch (_error) {
			this._fetchStatus = FetchStatus.FAILURE;
		}
	}

	@action.bound
	setBookingCardId(creditCardId: number | string | undefined, isOnetime?: boolean) {
		this._isInitialCardDeleted = false;
		this._isInitialCardExpired = false;

		if (isOnetime) {
			this._attachedBookingCardId = undefined;
			this._oneTimeCardId = creditCardId as string | undefined;
		} else {
			this._oneTimeCardId = undefined;
			this._attachedBookingCardId = creditCardId as number | undefined;
		}
	}

	@action.bound
	async addCardToBooking(bookingId: number) {
		try {
			const response = await creditCardsApi.addCreditCardToBooking({
				bookingId,
				customerUserCreditCardId: this._attachedBookingCardId || 0,
			});
			const {
				data: { error },
			} = response;
			if (error) {
				this.rootStore.alertStore.setAlert(error, 'error');
				this._fetchStatus = FetchStatus.FAILURE;
				return;
			}
			return true;
		} catch (_) {
			this._fetchStatus = FetchStatus.FAILURE;
			return;
		}
	}

	@action.bound
	async addOneTimeCardToBooking(bookingId: number) {
		try {
			const paymentMethodId = `${this._oneTimeCardId || ''}`;
			const response = await creditCardsApi.addOnetimeCardToBooking({
				bookingId,
				paymentMethodId,
			});
			const {
				data: { error },
			} = response;
			if (error) {
				this.rootStore.alertStore.setAlert(error, 'error');
				this._fetchStatus = FetchStatus.FAILURE;
				return;
			}
			return true;
		} catch (_) {
			this._fetchStatus = FetchStatus.FAILURE;
			return;
		}
	}

	@action.bound
	clearBookingCreditCards() {
		this._creditCardsForBooking = undefined;
		this._attachedBookingCardId = undefined;
		this._oneTimeCreditCards = [];
		this._oneTimeCardId = undefined;
	}
}
